/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';
import template from './product-list-page.component.html';

import {AdminListColumn, AdminResourceListPageBase} from '../../common/controller/admin-resource-list-page-base';
import {Uuid} from '../../../components/util/uuid';
import {LineItemTemplateResource} from '../../../components/api/resources/resource/line-item.resource';
import {NetworkDataResource} from '../../../components/api/resources/resource/network-data.resource';
import {ProductResource} from '../../../components/api/resources/resource/product.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-product-list-page',
  template
})
export class ProductListPageComponent extends AdminResourceListPageBase implements OnInit {
  readonly itemStateName = 'app.adm.products.product';

  columns: AdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: '', label: 'allowedDimensions', sortable: false},
    {param: 'layouts', label: 'layouts', sortable: false}
  ];

  productInfo: {[id: string]: any} = {};
  productSelections: any = {data: {filter: []}};
  targetingSelections: any = {data: {filter: []}};
  lineItemTemplates: any[] = [];
  isSetUp = false;
  isTargetingSetUp = false;
  editsMade = false;
  editsTargetingMade = false;
  productSelectionPass = false;
  productSelectionError = false;
  targetingSelectionPass = false;
  targetingSelectionError = false;
  private productQuery: Promise<void>;
  private previousProductSelections: any;
  private previousTargetingSelections: any;

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(ProductResource) private Product: any,
    @Inject(NetworkDataResource) private NetworkData: any,
    @Inject(LineItemTemplateResource) private LineItemTemplate: any,
    private ngZone: NgZone
  ) {
    super(LocalNetworkProfile);
  }

  get productInfoArray(): any[] {
    return Object.values(this.productInfo);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.productQuery = this.Product.query({minimal: true, pageSize: 101}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.productInfo = {};
        _.forEach(page.results, (item: any) => {
          this.productInfo[item.id] = {id: item.id, name: item.name};
        });
      });
    });

    this.NetworkData.get({filterByValue: 'productSelection', filterBy: 'type'}, (page: any) => {
      this.ngZone.run(() => {
        if (page.results.length) {
          const entry = page.results[0];
          try {
            entry.data.filter = JSON.parse(entry.data.filter);
            this.productSelections = new this.NetworkData(_.cloneDeep(entry));
            this.cleanData('productSelections').then(() => {
              this.previousProductSelections = _.cloneDeep(this.productSelections);
            });
          } catch (e) {
            // ignore
          }
        }
        this.isSetUp = true;
      });
    });

    this.LineItemTemplate.query().$promise.then((data: any) => {
      this.ngZone.run(() => {
        this.lineItemTemplates = data.results;
      });
    });

    this.NetworkData.get({filterByValue: 'targetingSelection', filterBy: 'type'}, (page: any) => {
      this.ngZone.run(() => {
        if (page.results.length) {
          const entry = page.results[0];
          try {
            entry.data.filter = JSON.parse(entry.data.filter);
            this.targetingSelections = new this.NetworkData(_.cloneDeep(entry));
            this.cleanData('targetingSelections').then(() => {
              this.previousTargetingSelections = _.cloneDeep(this.targetingSelections);
            });
          } catch (e) {
            // ignore
          }
        }
        this.isTargetingSetUp = true;
      });
    });
  }

  private cleanData(param: string): Promise<void> {
    return Promise.resolve(this.productQuery).then(() => {
      const holder = this[param];
      const isTargetingSelections = param === 'targetingSelections';
      holder.data = holder.data || {};
      holder.data.filter = this.normalizeFilter(holder.data.filter);

      _.forEach(_.get(holder, ['data', 'filter']) || [], (ps: any) => {
        delete ps.editable;
        delete ps.deleteMe;
        ps.key = _.isString(ps.key) ? ps.key : '';
        ps.products = this.normalizeStringArray(ps.products);
        ps.options = this.normalizeOptions(ps.options);

        if (ps.products) {
          ps.products = _.filter(ps.products, (productId: string) => !!this.productInfo[productId]);
        }

        _.forEach(ps.options, (opt: any) => {
          delete opt.deleteMe;
          if (_.trim(ps.key).length < 1) {
            ps.deleteMe = true;
          }

          if (isTargetingSelections) {
            const hasTitle = _.trim(opt.value).length > 0;
            const hasDescription = _.trim(opt.description).length > 0;
            const hasLineItemTemplate = !!_.get(opt, ['lineItemTemplate']);
            if (!hasTitle && !hasDescription && !hasLineItemTemplate) {
              opt.deleteMe = true;
            }
          } else if (!(_.trim(opt.value).length > 0 && _.get(opt, ['products'], []).length > 0)) {
            opt.deleteMe = true;
          }

          if (opt.products) {
            opt.products = _.filter(opt.products, (productId: string) => !!this.productInfo[productId]);
          }
        });
      });

      holder.data.filter = _.filter(_.get(holder, ['data', 'filter']) || [], (ps: any) => {
        if (!ps.deleteMe) {
          ps.options = _.filter(ps.options, (opt: any) => !opt.deleteMe);
        }
        return !ps.deleteMe;
      });
    });
  }

  private normalizeFilter(rawFilter: any): any[] {
    const parsedFilter = this.parseJsonIfString(rawFilter);
    return Array.isArray(parsedFilter) ? parsedFilter : [];
  }

  private normalizeOptions(rawOptions: any): any[] {
    const parsedOptions = this.parseJsonIfString(rawOptions);
    return Array.isArray(parsedOptions) ? parsedOptions : [];
  }

  private normalizeStringArray(rawValues: any): string[] {
    const parsedValues = this.parseJsonIfString(rawValues);
    return Array.isArray(parsedValues) ? parsedValues.filter((value) => _.isString(value)) : [];
  }

  private parseJsonIfString(value: any): any {
    if (!_.isString(value)) {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  isProductInList(products: string[], id: string): boolean {
    return (products || []).indexOf(id) > -1;
  }

  toggleProductInList(products: string[], id: string): void {
    const idx = (products || []).indexOf(id);
    if (idx > -1) {
      products.splice(idx, 1);
    } else {
      products.push(id);
    }
    this.editsMade = true;
  }

  // Product selections methods
  setUpOptions(): void {
    this.editsMade = true;
    const emptyEntry = {key: '', editable: true, options: [{value: '', products: []}]};
    if (!this.productSelections.savePartial) {
      this.productSelections = new this.NetworkData({
        id: Uuid.generate(),
        name: 'Product Selection',
        description: 'Product selection options',
        type: 'productSelection',
        data: {filter: [emptyEntry]}
      });
      return;
    }
    this.productSelections.data.filter.push(emptyEntry);
  }

  deleteOption(index: number): void {
    this.editsMade = true;
    _.pullAt(this.productSelections.data.filter, index);
  }

  addValue(parentIndex: number): void {
    this.editsMade = true;
    this.productSelections.data.filter[parentIndex].options.push({value: '', products: []});
  }

  removeValue(parentIndex: number, optionIndex: number): void {
    this.editsMade = true;
    _.pullAt(this.productSelections.data.filter[parentIndex].options, optionIndex);
  }

  edit(): void {
    this.editsMade = true;
  }

  saveProductSelections(): void {
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
    this.cleanData('productSelections').then(() => {
      this.productSelections.savePartial().then(() => {
        this.ngZone.run(() => {
          this.productSelectionPass = true;
          this.previousProductSelections = _.cloneDeep(this.productSelections);
        });
      }).catch(() => {
        this.ngZone.run(() => {
          this.productSelectionError = true;
        });
      });
    });
  }

  cancelProductSelections(): void {
    this.productSelections = _.cloneDeep(this.previousProductSelections);
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
  }

  // Targeting selections methods
  setUpTargetingOptions(): void {
    this.editsTargetingMade = true;
    const emptyEntry = {key: '', products: [], editable: true, options: [this.createEmptyTargetingOption()]};
    if (!this.targetingSelections.savePartial) {
      this.targetingSelections = new this.NetworkData({
        id: Uuid.generate(),
        name: 'Targeting Selection',
        description: 'Targeting selection options',
        type: 'targetingSelection',
        data: {filter: [emptyEntry]}
      });
      return;
    }
    this.targetingSelections.data.filter.push(emptyEntry);
  }

  deleteTargetingOption(index: number): void {
    this.editsTargetingMade = true;
    _.pullAt(this.targetingSelections.data.filter, index);
  }

  openTargetingEditor(ps: any): void {
    ps.options = this.normalizeOptions(ps.options);
    if (!ps.options.length) {
      ps.options.push(this.createEmptyTargetingOption());
    }
    ps.editable = true;
  }

  addTargetingValue(parentIndex: number): void {
    this.editsTargetingMade = true;
    this.targetingSelections.data.filter[parentIndex].options.push(this.createEmptyTargetingOption());
  }

  removeTargetingValue(parentIndex: number, optionIndex: number): void {
    this.editsTargetingMade = true;
    _.pullAt(this.targetingSelections.data.filter[parentIndex].options, optionIndex);
  }

  editTargeting(): void {
    this.editsTargetingMade = true;
  }

  saveTargetingSelections(): void {
    this.editsTargetingMade = false;
    this.targetingSelectionPass = false;
    this.targetingSelectionError = false;
    this.cleanData('targetingSelections').then(() => {
      this.targetingSelections.savePartial().then(() => {
        this.ngZone.run(() => {
          this.targetingSelectionPass = true;
          this.previousTargetingSelections = _.cloneDeep(this.targetingSelections);
        });
      }).catch(() => {
        this.ngZone.run(() => {
          this.targetingSelectionError = true;
        });
      });
    });
  }

  cancelTargetingSelections(): void {
    this.targetingSelections = _.cloneDeep(this.previousTargetingSelections);
    this.editsTargetingMade = false;
    this.targetingSelectionPass = false;
    this.targetingSelectionError = false;
  }

  private createEmptyTargetingOption(): any {
    return {value: '', description: '', lineItemTemplate: null};
  }
}
